"use client";

import { MdAddPhotoAlternate } from "react-icons/md";
import styles from "./photo-uploader.module.css";
import { IoMdClose } from "react-icons/io";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDropzone, FileRejection, ErrorCode } from "react-dropzone";
import Image from "next/image";
import { toast } from "react-toastify";
import { v4 as uuidv4 } from "uuid";
import { handleUploadProfilePhoto } from "../handlers";
import { useUserDataContext } from "../user-data-context";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useTranslations } from "next-intl";

const config = {
  quality: 0.9,
  maxWidth: 512,
  maxHeight: 512,
  autoRotate: true,
  mimeType: "image/jpeg",
};

let readAndCompressImage: any;

export default function PhotoUploader({
  onCloseAction,
}: {
  onCloseAction: () => void;
}) {
  const { userData } = useUserAuthDataContext();
  const { state, updateField } = useUserDataContext();
  const [file, setFile] = useState<File>();
  const [isImageLoading, setIsImageLoading] = useState(false);
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const t = useTranslations("MyAccount.PhotoUploader");

  useEffect(() => {
    // Dynamically import browser-image-resizer in the browser
    const loadLibrary = async () => {
      const resizer = await import("browser-image-resizer");
      readAndCompressImage = resizer.readAndCompressImage;
    };
    loadLibrary();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toggleRef.current &&
        !toggleRef.current.contains(event.target as Node)
      ) {
        onCloseAction();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const [imageSrc, setImageSrc] = useState<string>();
  const onDrop = useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length !== 0) {
        switch (rejectedFiles[0].errors[0].code) {
          case ErrorCode.FileInvalidType:
            toast(t("formatError"), {
              type: "error",
            });
            break;
          case ErrorCode.FileTooLarge:
            toast(t("tooLargeError"), {
              type: "error",
            });
            break;
          case ErrorCode.TooManyFiles:
            toast(t("onlyOnePhotoError"), {
              type: "error",
            });
            break;
          default:
            toast(t("unknownError"), {
              type: "error",
            });
            break;
        }
        return;
      }
      setIsImageLoading((prev) => true);
      const resizedImage: File = await readAndCompressImage(
        acceptedFiles[0],
        config
      );
      const extension = "jpeg";
      const uuidString = uuidv4();
      const newName = `${uuidString}.${extension}`;
      const resizedImageFile = new File([resizedImage], newName, {
        type: "image/jpeg",
      });
      const imageUrl = URL.createObjectURL(resizedImageFile);
      setFile(resizedImageFile);
      setImageSrc(imageUrl);
      setIsImageLoading((prev) => false);
    },
    []
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    maxFiles: 1,
    maxSize: 3 * 1024 * 1024,
  });
  return (
    <div className={styles.uploaderOverlay} role="dialog" aria-modal="true">
      <div ref={toggleRef} className={styles.uploaderWrapper}>
        <div className={styles.topWrapper}>
          <h3>{t("title")}</h3>
          <button
            onClick={onCloseAction}
            className={styles.closeButton}
            aria-label={t("ariaCloseUploader")}
          >
            <IoMdClose />
          </button>
        </div>
        {imageSrc && <p className={styles.description}>{t("description")}</p>}
        <div
          className={`${styles.dragzone} ${
            (imageSrc || isImageLoading) && styles.hasImage
          }`}
          {...getRootProps()}
          role="button"
          aria-label={t("ariaDragAndDrop")}
          aria-busy={isImageLoading}
        >
          <input {...getInputProps()} aria-hidden="true" />
          {!imageSrc && !isImageLoading && (
            <>
              <MdAddPhotoAlternate aria-hidden="true" />
              {isDragActive ? (
                <p>{t("hasPhotoDescription")}</p>
              ) : (
                <p className={styles.inputDescriptionNoPhoto}>
                  {t("description")}
                </p>
              )}
            </>
          )}
          {isImageLoading ? (
            <div
              className={`${styles.isLoading} ${styles.imageLoading}`}
              role="status"
            ></div>
          ) : (
            <>
              {imageSrc && (
                <Image
                  className={styles.addedImage}
                  src={imageSrc}
                  alt={t("ariaUploadedPhoto")}
                  width={500}
                  height={500}
                />
              )}
            </>
          )}
        </div>
        <button
          onClick={async () => {
            if (file === undefined || imageSrc === undefined) {
              toast(t("pleaseSelectPhoto"), {
                type: "error",
              });
              return;
            }
            await handleUploadProfilePhoto(
              file,
              state.photo_url,
              imageSrc,
              (newValue) => {
                updateField("photo_url", newValue);
              },
              onCloseAction,
              userData.sessionId
            );
          }}
          aria-label={t("ariaUploadPhoto")}
          className={styles.uploadButton}
        >
          {t("upload")}
        </button>
      </div>
    </div>
  );
}
